<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import RadioCardList, {
  type Choice,
} from "../components/form/RadioCardList.vue";
import PayerSelect from "../components/payer/PayerSelect.vue";
import { PaymentType, Rate, ResumeQuotaRate, createOrder } from "#/../js/api/order";
import { formatMoney as formatPrice, plural } from "../../js/utils";
import { getCurrentUserBalance } from "../../js/api/balance";
import { clearCurrentUserCart } from "../../js/api/cart";

interface OrderItem {
  type: 'vacancy' | 'resume';
  name: Rate | ResumeQuotaRate;
  params: number;
  duration: string;
  price: number;
}

const props = withDefaults(
    defineProps<{
      positions: OrderItem[] | string;
      amount: number | string;
      clearCartAfterSuccess: boolean;
    }>(),
    {
      clearCartAfterSuccess: false,
    }
);

const emit = defineEmits(["order-form:submit", "order-form:error"]);

const autoActivate = ref<boolean>(true);
const selectedPayerId = ref<string>("");
const selectedPayment = ref<PaymentType>("balance");
const companyBalance = ref(0);
const loading = ref(false);

const orderItems = computed<OrderItem[]>(() => {
  return "string" === typeof props.positions
      ? (JSON.parse(props.positions) as OrderItem[])
      : props.positions;
});

const loadBalance = async () => {
  loading.value = true;

  try {
    companyBalance.value = await getCurrentUserBalance();
  } finally {
    loading.value = false;
  }
};

const formattedBalance = (value: number) => {
  return (value / 100).toLocaleString("ru-RU");
};

const paymentChoices = computed<Choice[]>(() => [
  {
    value: "balance",
    title: `Списать со счета кабинета (Баланс: ${formattedBalance(companyBalance.value)} ₽)`,
    description: "Спишем стоимость с баланса кабинета",
    enabled:
        !!selectedPayerId.value && companyBalance.value >= totalAmount.value,
  },
  {
    value: "bill",
    title: "Оплата по счёту",
    description: "Сформируем счет для юридического лица",
    enabled: !!selectedPayerId.value,
  },
]);

const totalAmount = computed<number>(() =>
    typeof props.amount === "string"
        ? parseInt(props.amount)
        : Math.floor(props.amount)
);

const rateTitle = (item: OrderItem): string => {
  if (item.type === 'vacancy') {
    switch (item.name) {
      case "basic":
        return 'Вакансия "Стандарт"';
      case "optimum":
        return 'Вакансия "Оптимум"';
      default:
        return 'Вакансия';
    }
  } else if (item.type === 'resume') {
    switch (item.name) {
      case "resume_quota_3_days":
        return 'Квоты резюме (3 дня)';
      case "resume_quota_7_days":
        return 'Квоты резюме (7 дней)';
      case "resume_quota_30_days":
        return 'Квоты резюме (30 дней)';
      default:
        return 'Квоты резюме';
    }
  }
  return 'Неизвестная позиция';
};

const formHandling = ref(false);
const formSuccess = ref("");
const formError = ref("");

const handleSubmit = async (): Promise<void> => {
  if (formHandling.value === true) {
    return;
  }

  formHandling.value = true;
  formSuccess.value = "";
  formError.value = "";

  try {
    await createOrder({
      payerId: selectedPayerId.value,
      autoActivate: autoActivate.value,
      paymentType: selectedPayment.value,
      positions: orderItems.value.map((p) => {
        if (p.type === 'vacancy') {
          return {
            type: "vacancy_quota" as const,
            rate: p.name as Rate,
            count: p.params,
          };
        } else {
          return {
            type: "resume_quota" as const,
            rate: p.name as ResumeQuotaRate,
            count: p.params,
          };
        }
      }),
    });
    formSuccess.value = "Заказ успешно сформирован.";

    if (props.clearCartAfterSuccess) {
      await clearCurrentUserCart();
    }

    emit("order-form:submit");
  } catch (e) {
    formError.value = "Возникла ошибка во время формирования заказа.";
    formHandling.value = false;
    emit("order-form:error");
  }
};

onMounted(() => {
  loadBalance();
});
</script>

<template>
  <div v-if="formError" class="alert alert-error">{{ formError }}</div>

  <div v-if="formSuccess" class="alert alert-success">{{ formSuccess }}</div>

  <form v-else class="order-form" @submit.prevent="handleSubmit">
    <section class="section">
      <h2 class="section-title">Состав заказа</h2>

      <div class="order-table">
        <div class="table-header">
          <span>Позиция</span>
          <span>Параметры</span>
          <span>Срок действия</span>
          <span>Цена</span>
        </div>
        <div v-for="(item, index) in orderItems" :key="index" class="table-row">
          <span class="item-name">{{ rateTitle(item) }}</span>
          <span class="item-params">{{ item.params }}
            {{ plural(item.params, "штука", "штуки", "штук") }}</span>
          <span class="item-duration">{{ item.duration }}</span>
          <span class="item-price">{{ formatPrice(item.price) }}</span>
        </div>
      </div>
    </section>

    <label class="checkbox-row">
      <input v-model="autoActivate" class="form-control" type="checkbox" />
      <span class="checkbox-label">
        Активировать заказ автоматически, сразу после поступления средств
      </span>
    </label>

    <payer-select v-model="selectedPayerId" required />

    <div class="radio-list__inner">
      <p>Выберите способ оплаты <span class="red">*</span></p>

      <RadioCardList
          v-model="selectedPayment"
          :choices="paymentChoices"
          :required="true"
      />
    </div>

    <hr />

    <div class="order-form__total">
      <div class="order-form__total-inner">
        <span class="order-form__total-label">К оплате:</span>
        <span class="order-form__total-amount">
          {{ formatPrice(totalAmount) }}
        </span>
      </div>

      <button
          type="submit"
          class="order-form__submit-btn btn"
          :disabled="formHandling"
          @click="handleSubmit"
      >
        Выписать счёт
      </button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.radio-list {
  &__inner {
    margin-bottom: 60px;
    p {
      margin-top: 32px;
      margin-bottom: 16px;
    }
  }
}

.section-title {
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: -4%;
}

.order-table {
  width: 100%;
  margin-bottom: 44px;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1.2fr;
  padding: 10px 0;
  border-bottom: 1.5px solid #e0e0e0;
  font-weight: 700;
  font-size: 14px;
  color: #222;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1.2fr;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  align-items: center;
}

.item-price {
  font-weight: 700;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 38px;
  cursor: pointer;
  user-select: none;
}

.order-form {
  hr {
    color: #c5c5c5;
  }

  &__total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-family: Inter, sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 100%;
    letter-spacing: -4%;
    margin-top: 38px;
  }

  &__total-inner {
    display: flex;
    align-items: baseline;
    gap: 25px;
    color: #000000;
    font-size: 18px;
    font-weight: 700;
  }
}
</style>
